import React, { useState, useEffect, Fragment } from "react";
import { useParams, useSearchParams, useNavigate, useLocation } from "react-router-dom";
import api from "../services/api";
import Loader from "../components/loader";
import { toast } from "react-hot-toast";
import { FiExternalLink, FiPrinter } from "react-icons/fi";
import { useSelector, useDispatch } from "react-redux";
import { setOrganisation, setUser } from "../redux/auth/actions";

export default function BudgetSignature() {
  const { id } = useParams();
  const user = useSelector((state) => state.Auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [downloading, setDownloading] = useState(false);

  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roadlogItems, setRoadlogItems] = useState([]);
  const [modules, setModules] = useState([]);
  const [searchParams] = useSearchParams();

  const [signed, setSigned] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [signingLoading, setSigningLoading] = useState(false);

  const token = searchParams.get("token");
  const [signingTokenInfo, setSigningTokenInfo] = useState(null);

  const getBudgetWithToken = async () => {
    try {
      const { ok, data } = await api.get(`/budget/token/${token}`);
      if (!ok) throw new Error("Failed to fetch budget");
      setBudget(data);
    } catch (e) {
      console.log(e);
      toast.error("Failed to fetch budget");
    }
    setLoading(false);
  };

  const getBudgetById = async () => {
    try {
      const { ok, data } = await api.get(`/budget/public/${id}`);
      if (!ok) throw new Error("Failed to fetch budget");
      setBudget(data);
    } catch (e) {
      console.log(e);
      toast.error("Failed to fetch budget");
    }
    setLoading(false);
  };

  const getModules = async () => {
    try {
      const { ok, data } = await api.post(`/roadlog_module/public/search/`, {
        project_id: budget.projectId,
      });
      if (!ok) throw new Error("Failed to fetch modules");
      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const getRoadlogItems = async () => {
    try {
      const { ok, data } = await api.post(`/roadlog_item/public/search/`, {
        project_id: budget.projectId,
        budget_id: budget._id,
        sort: "-estimation",
      });
      if (!ok) throw new Error("Failed to fetch roadlog items");
      return data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  const organizeData = async () => {
    const modulesList = await getModules();
    const itemsList = await getRoadlogItems();
    setRoadlogItems(itemsList);

    // Process modules and add items to each module
    const modulesWithItems = modulesList.map((module) => {
      const moduleItems = itemsList.filter((item) => String(item.module_id) === String(module._id));
      return {
        ...module,
        items: moduleItems,
      };
    });

    // Check for items without module
    const itemsWithoutModule = itemsList.filter((item) => !item.module_id);
    if (itemsWithoutModule.length > 0) {
      modulesWithItems.push({
        _id: "general",
        title: "General Features",
        items: itemsWithoutModule,
      });
    }

    setModules(modulesWithItems);
  };

  const handlePrint = async () => {
    setDownloading(true);
    try {
      const { data, ok } = await api.post(`/budget/${budget._id}/pdf`);
      if (!ok) throw new Error("Failed to generate PDF");
      toast.success("PDF generated");
      // open file url in new tab
      window.open(data.files[data.files.length - 1], "_blank");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
    setDownloading(false);
  };

  async function fetchData() {
    try {
      const res = await api.get("/user/signin_token");
      if (!res.ok || !res.user) return setLoading(false);
      if (res.token) api.setToken(res.token);
      dispatch(setUser(res.user));

      if (res.user.organisation_id) {
        const { data, ok } = await api.get(`/organisation/${res.user.organisation_id}`);
        if (!ok) throw new Error("Error fetching organisation");
        dispatch(setOrganisation(data));
      }
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!token) return;
    getBudgetWithToken();
  }, [token]);

  useEffect(() => {
    if (!id) return;
    getBudgetById();
  }, [id]);

  useEffect(() => {
    if (!budget || !token || loading) return;

    const tokenInfo = budget.signing_tokens?.find((t) => t.token === token);
    setSigningTokenInfo(tokenInfo);

    if (!user) {
      const currentUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`/auth?redirect=${currentUrl}`);
      return;
    }

    if (user && tokenInfo && user.email.toLowerCase() !== tokenInfo.email.toLowerCase())
      return toast.error(`This signature link is intended for ${tokenInfo.email}. You are connected with ${user.email}. Please disconnect and login with the correct account.`);
  }, [budget, user, token, location, loading]);

  useEffect(() => {
    if (!budget) return;
    if (budget.facturation_type === "forfait") {
      organizeData();
    }
  }, [budget]);

  if (loading)
    return (
      <div className="bg-[#ededeb] min-h-full p-4 sm:p-8 w-full flex justify-center">
        <div className="w-[210mm] min-h-[297mm] bg-white rounded-lg shadow-lg mx-auto">
          <div className="p-[1.5cm]">
            <p className="text-center mt-2 italic">Loading budget details...</p>
            <Loader />
          </div>
        </div>
      </div>
    );

  if (!budget)
    return (
      <div className="bg-[#ededeb] h-full p-8 w-full">
        <div className="w-3/4 mx-auto bg-white rounded-lg p-8 shadow-lg">
          <div className="text-center">
            <div className="text-red-600 flex items-center justify-center text-xl">
              <svg className="w-8 h-8 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              Seems that nothing is here
            </div>
            <p className="mt-4 text-gray-600">
              If you have any questions, please contact{" "}
              <a href="mailto:office@selego.co" className="text-sky-600 hover:underline">
                office@selego.co
              </a>
            </p>
          </div>
        </div>
      </div>
    );

  const handleConfirmSign = async () => {
    setSigningLoading(true);

    try {
      if (!token) return toast.error("You must receive a link to sign the budget");

      if (!user) return toast.error("You must be logged in to sign");

      if (signingTokenInfo && user.email.toLowerCase() !== signingTokenInfo.email.toLowerCase()) return toast.error("Your email does not match the recipient of this signing link");

      const { ok, data } = await api.put(`/budget/${budget._id}/sign`, {
        token,
      });

      if (!ok) throw new Error("Failed to sign budget");
      setSigned(true);
    } catch (e) {
      toast.error("Failed to sign budget");
      console.log(e);
    } finally {
      setSigningLoading(false);
    }
    setShowConfirmModal(false);
  };
  if (signed)
    return (
      <div className="bg-[#ededeb] h-full p-4 sm:p-8 w-full">
        <div className="w-full md:w-3/4 mx-auto bg-white rounded-lg p-4 sm:p-8 shadow-lg">
          <div className="text-center">
            <div className="text-green-600 flex items-center justify-center text-lg sm:text-xl">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              Budget successfully signed
            </div>
            <p className="mt-4 text-sm sm:text-base text-black">
              If you have any questions, please contact{" "}
              <a href="mailto:office@selego.co" className="text-sky-600 hover:underline">
                office@selego.co
              </a>
            </p>
            <div className="mt-4 text-center flex justify-center">
              <a href={`/project/${budget.projectId}/budget-normal`} className="cursor-pointer hover:underline flex items-center gap-1">
                Click to be redirected to <strong>{budget.projectName}</strong>
                <FiExternalLink className="inline-block ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );

  return (
    <div className="relative bg-[#ededeb] min-h-full p-4 sm:p-8 w-full flex flex-col justify-center">
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Confirm signature</h3>
            <p className="text-black mb-6">By signing, you agree to the terms and conditions of the budget.</p>
            <div className="flex justify-end space-x-4">
              <button onClick={() => setShowConfirmModal(false)} className="px-4 py-2 text-black hover:underline">
                Cancel
              </button>
              <button
                disabled={signingLoading}
                onClick={handleConfirmSign}
                className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50">
                {signingLoading ? "Confirming..." : "Confirm sign"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="w-[210mm] mx-auto mb-4">
        {user && (
          <p className="text-sm font-medium text-black">
            Connected as {user.name} ({user.email})
          </p>
        )}
      </div>

      {user && user.role === "admin" && (
        <div className="flex justify-end w-[210mm] mx-auto mb-2">
          <button onClick={handlePrint} className="btn blue-btn flex gap-1" disabled={downloading}>
            <FiPrinter className="w-4 h-4" />
            {downloading ? "Printing..." : "Print Budget"}
          </button>
        </div>
      )}

      <div className="w-[210mm] bg-white rounded-lg shadow-lg mx-auto ">
        <div className="p-[1.5cm]">
          <div className="border-b pb-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4">
            <div className="">
              <div className="flex items-center mb-1">
                <div className="flex items-center">
                  {budget.projectLogo && <img src={budget.projectLogo} alt={budget.projectName} className="w-10 h-10 mr-2 object-contain" />}
                  <span className="text-base sm:text-lg text-black">{budget.projectName}</span>
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-black">{budget.name}</h1>
            </div>

            {budget.frequency === "monthly" ? (
              <div className="bg-sky-50 p-4 rounded-lg w-full sm:w-auto">
                <p className="text-sky-800">Budget</p>
                <h2 className="text-xl sm:text-2xl font-bold text-sky-800">{budget.value.toLocaleString()}€/month</h2>
              </div>
            ) : (
              <div className="bg-sky-50 p-4 rounded-lg w-full sm:w-auto">
                <p className="text-sky-800">Budget</p>
                <h2 className="text-xl sm:text-2xl font-bold text-sky-800">{budget.amount.toLocaleString()}€</h2>
              </div>
            )}
          </div>

          <div className="grid gap-6">
            <div className="grid gap-4">
              <div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  {!budget.signed_at ? (
                    <div className="space-y-4 flex flex-col items-center">
                      <p className="text-black text-center">This budget requires your signature to be approved.</p>
                      <button onClick={() => setShowConfirmModal(true)} className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-700 transition-colors">
                        Sign
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <svg className="w-10 h-10 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <div>
                          Signed on{" "}
                          {new Date(budget.signed_at).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                          , {new Date(budget.signed_at).toLocaleTimeString()}
                        </div>
                        <div>by {budget.signed_by_name}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {budget.description && (
                <div className="border-b pb-8">
                  <h3 className="font-semibold text-black mb-2">Description</h3>
                  <div
                    style={{ marginTop: "12px", color: "#374151", fontSize: "14px", lineHeight: "1.5" }}
                    className="comment-content"
                    dangerouslySetInnerHTML={{ __html: budget.description }}
                  />{" "}
                </div>
              )}
              {budget.legal && (
                <div className="border-b pb-8">
                  <h3 className="font-semibold text-black mb-2">Legal</h3>
                  <p className="text-black whitespace-pre-wrap">{budget.legal}</p>
                </div>
              )}
              {budget.facturation_type === "forfait" && roadlogItems.length > 0 && (
                <div className="border-b pb-8">
                  <h3 className="font-semibold text-black mb-2">Features included in this budget</h3>
                  <div className="space-y-6">
                    {modules.map(
                      (module) =>
                        module.items &&
                        module.items.length > 0 && (
                          <div key={module._id} className="border border-gray-200 rounded-md overflow-hidden">
                            <h4 className="bg-slate-200 px-4 py-2.5 text-base font-medium text-gray-700 border-b border-gray-200">{module.title}</h4>
                            <div className="divide-y divide-gray-200">
                              {module.items
                                .sort((a, b) => Number(a.index) - Number(b.index))
                                .map((item) => (
                                  <div key={item._id} className="px-4 py-4">
                                    <div className="w-full flex justify-between items-center gap-4">
                                      <p className="font-medium text-gray-900">{item.title}</p>
                                      <div className="flex items-center gap-6 flex-shrink-0">
                                        <div className="flex items-center gap-2">
                                          <PriorityBadge priority={item.nnl} />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-gray-600">{item.days_required ? `${item.days_required} days` : "-"}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-bold text-gray-800">{item.estimation ? `${Number(item.estimation).toLocaleString("fr-FR")} €` : "-"}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ),
                    )}
                  </div>
                </div>
              )}
              <div className="border-b pb-8">
                <h3 className="font-semibold text-black mb-2">Budget details</h3>
                <div className="space-y-2 text-gray-900">
                  <p>
                    {budget.facturation_type === "regie" && "This budget is based on actual time spent and resources used (Time & Materials)"}
                    {budget.facturation_type === "forfait" && "This budget is Fixed-price with predefined scope and deliverables"}
                  </p>
                  {budget.startAt && budget.endAt && (
                    <p>
                      Application period :&nbsp;
                      {new Date(budget.startAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                      &nbsp;-&nbsp;
                      {new Date(budget.endAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  )}
                </div>
              </div>
              <div className="border-b pb-8">
                <h3 className="font-semibold text-black mb-2">Terms and conditions</h3>
                <p className="text-black whitespace-pre-wrap">
                  In the event of a contract between the parties, the terms of that contract shall prevail in accordance with Article 1103 of the French Civil Code. In the absence
                  of such a contract, the applicable provisions of the French Civil Code and/or Commercial Code shall apply.
                </p>
              </div>
              <p className="text-black">
                If you have any questions, please contact{" "}
                <a href="mailto:office@selego.co" className="text-sky-600 hover:underline">
                  office@selego.co
                </a>
              </p>
            </div>
            <div className="mt-4 text-center flex justify-center">
              <a href={`/project/${budget.projectId}/budget-normal`} className="cursor-pointer hover:underline flex items-center gap-1">
                Click to be redirected to <strong>{budget.projectName}</strong>
                <FiExternalLink className="inline-block ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <style jsx global>{`
        /* Force empty paragraphs to display with spacing */
        .comment-content p:empty {
          display: block;
          margin: 1rem 0;
          height: 1rem;
        }

        /* Alternative: target paragraphs with only whitespace */
        .comment-content p:empty,
        .comment-content p:blank {
          display: block !important;
          margin: 1rem 0 !important;
          height: 1rem !important;
          line-height: 1rem !important;
        }

        @media print {
          @page {
            size: A4 portrait;
            margin: 1.5cm;
          }

          html,
          body {
            height: auto !important;
            min-height: auto !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }

          /* Container adjustments */
          .max-w-7xl {
            width: 100% !important;
            max-width: none !important;
            margin: 0 !important;
            overflow: visible !important;
            height: auto !important;
          }

          /* Content flow */
          .space-y-6 > div,
          .space-y-8 > div {
            display: block !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Epic containers */
          .divide-y > div {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-bottom: 1rem !important;
          }

          /* Headers */
          h1,
          h2,
          h3,
          h4 {
            page-break-after: avoid !important;
            break-after: avoid !important;
          }

          /* Keep flex for the epic title and metadata row */
          .print\\:flex {
            display: flex !important;
          }

          /* Keep items aligned in the center */
          .print\\:items-center {
            align-items: center !important;
          }

          /* Keep proper spacing between items */
          .print\\:gap-4 {
            gap: 1rem !important;
          }

          /* Force show backgrounds */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          /* Remove any fixed heights */
          .min-h-screen,
          .h-screen {
            height: auto !important;
            min-height: 0 !important;
          }

          /* Ensure content breaks properly */
          section {
            display: block !important;
            page-break-inside: auto !important;
            break-inside: auto !important;
          }

          /* Remove any transforms that might affect printing */
          * {
            transform: none !important;
          }

          /* Ensure proper spacing between elements */
          .space-y-8 > * + * {
            margin-top: 2rem !important;
          }

          /* Remove any overflow restrictions */
          .overflow-hidden,
          .overflow-x-hidden,
          .overflow-y-hidden {
            overflow: visible !important;
          }

          /* Hide print button when printing */
          .print\\:hidden {
            display: none !important;
          }

          /* Ensure each module starts on a new page if needed */
          .print\\:break-inside-avoid-page {
            page-break-inside: avoid !important;
            break-inside: avoid-page !important;
          }

          /* Make sure epic blocks display properly */
          .divide-y > div {
            display: block !important;
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }

          /* Ensure module headers stay with their content */
          h4 + .divide-y {
            page-break-before: avoid !important;
            break-before: avoid !important;
          }

          /* Better spacing for printed content */
          .px-4.py-4 {
            padding: 1rem !important;
          }

          /* Ensure proper display of flex items in print */
          .flex.items-center,
          .flex.justify-between {
            display: flex !important;
          }

          /* Center the first page content */
          body {
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
          }

          /* Add top margin to the first page */
          .max-w-7xl {
            margin-top: 2cm !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }

          /* First page padding */
          .max-w-7xl > div:first-child {
            padding-top: 1cm !important;
          }
        }
      `}</style>
    </div>
  );
}

function PriorityBadge({ priority }) {
  if (!priority) return <span className="text-xs text-gray-400">No priority</span>;

  let bgColor = "bg-gray-100";
  let textColor = "text-gray-800";

  if (priority === "NOW") {
    bgColor = "bg-red-100";
    textColor = "text-red-800";
  } else if (priority === "NEXT") {
    bgColor = "bg-orange-100";
    textColor = "text-orange-800";
  }

  return <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${bgColor} ${textColor}`}>{priority}</span>;
}
